import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SetupGuide from "../components/SetupGuide";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { useAuth, hasRole } from "../lib/auth";
import { toast } from "sonner";

export default function AssetRooms() {
  const { user } = useAuth();
  const canEdit = hasRole(user, "admin", "accounts", "hk_supervisor");
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [lines, setLines] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const localToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
  const [entryDate, setEntryDate] = useState(localToday());
  const [minEntryDate, setMinEntryDate] = useState("");
  const [todaySubmitted, setTodaySubmitted] = useState(false);
  const [submittedCl, setSubmittedCl] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);

  useEffect(() => {
    api.get("/rooms").then(({ data }) => setRooms(data));
    api.get("/assets/items", { params: { active: true } }).then(({ data }) => setItems(data));
  }, []);

  const loadRoom = async (rid, selectedDate = entryDate) => {
    setRoomId(rid);
    const [{ data: d }, { data: cl }, { data: rng }] = await Promise.all([
      api.get(`/assets/rooms/${rid}/amenities`),
      api.get("/assets/checklists", { params: { room_id: rid } }),
      api.get(`/assets/rooms/${rid}/entry-date-range`),
    ]);
    setData(d); setChecklists(cl);
    if (rng?.min_date) setMinEntryDate(rng.min_date);
    const todayCl = cl.find(c => (c.effective_date || c.date) === selectedDate && c.status === "submitted");
    setTodaySubmitted(!!todayCl);
    setSubmittedCl(todayCl || null);
    // Build checklist lines from active templates
    const tplLines = {};
    (d.templates || []).forEach(t => t.lines.forEach(l => {
      tplLines[l.item_id] = { item_id: l.item_id, required: l.qty || 0, mandatory: l.mandatory };
    }));
    setLines(Object.values(tplLines).map(l => ({
      ...l, previous: d.room_balances[l.item_id] || 0,
      issued: 0, consumed: 0, returned: 0, missing: 0, damaged: 0,
    })));
  };

  const itemName = id => items.find(i => i.id === id)?.name || id;
  const setLine = (idx, field, v) => setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: v } : l));

  const submitChecklist = async () => {
    if (submitBusy) return;
    setSubmitBusy(true);
    try {
      const payload = {
        room_id: roomId,
        effective_date: entryDate,
        lines: lines.map(l => ({
          item_id: l.item_id, required: Number(l.required || 0), previous: Number(l.previous || 0),
          issued: Number(l.issued || 0), consumed: Number(l.consumed || 0), returned: Number(l.returned || 0),
          missing: Number(l.missing || 0), damaged: Number(l.damaged || 0),
        })),
      };
      const { data: draft } = await api.post("/assets/checklists", payload);
      if (draft.already_submitted) {
        toast.info(`Checklist for ${entryDate} is already submitted. Existing entry loaded.`);
        return loadRoom(roomId, entryDate);
      }
      const { data: res } = await api.post(`/assets/checklists/${draft.id}/submit`);
      toast.success(`Checklist submitted — ${res.txns} movements, ${res.incidents} incident(s)`);
      loadRoom(roomId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitBusy(false); }
  };

  const openEdit = () => {
    if (!submittedCl) return;
    setEditLines((submittedCl.lines || []).map(l => ({ ...l })));
    setEditReason(""); setShowPreview(false); setEditOpen(true);
  };
  const setEditLine = (idx, field, v) => setEditLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: v } : l));
  const changedLines = () => {
    const orig = {}; (submittedCl?.lines || []).forEach(l => { orig[l.item_id] = l; });
    const fields = ["issued", "consumed", "returned", "missing", "damaged"];
    return editLines.filter(l => fields.some(f => Number(l[f] || 0) !== Number((orig[l.item_id] || {})[f] || 0)))
      .map(l => ({ line: l, orig: orig[l.item_id] || {}, fields }));
  };
  const reviseChecklist = async () => {
    setEditBusy(true);
    try {
      const payload = {
        room_id: roomId, remarks: editReason || null,
        effective_date: submittedCl.effective_date || submittedCl.date,
        lines: editLines.map(l => ({
          item_id: l.item_id, required: Number(l.required || 0), previous: Number(l.previous || 0),
          issued: Number(l.issued || 0), consumed: Number(l.consumed || 0), returned: Number(l.returned || 0),
          missing: Number(l.missing || 0), damaged: Number(l.damaged || 0),
        })),
      };
      const { data: res } = await api.post(`/assets/checklists/${submittedCl.id}/revise`, payload);
      toast.success(`Checklist updated — ${res.txns} movements re-posted`);
      setEditOpen(false); loadRoom(roomId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEditBusy(false); }
  };

  return (
    <Layout>
      <div className="mb-4"><h1 className="section-title">Room Assets & Amenities</h1></div>
      <AssetNav />
      <SetupGuide />
      <Card className="p-4 mb-4 max-w-sm">
        <Select value={roomId} onValueChange={loadRoom}>
          <SelectTrigger className="h-11" data-testid="room-select"><SelectValue placeholder="Select a room" /></SelectTrigger>
          <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number} · {r.status}</SelectItem>)}</SelectContent>
        </Select>
        <div className="mt-3">
          <label className="text-sm text-slate-600">Entry Date</label>
          <Input
            type="date"
            value={entryDate}
            min={minEntryDate || undefined}
            max={localToday()}
            onChange={event => {
              const value = event.target.value;
              setEntryDate(value);
              if (roomId) loadRoom(roomId, value);
            }}
            className="h-11 mt-1"
            data-testid="amenity-entry-date"
          />
        </div>
      </Card>

      {data && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="outline" className="border-slate-300">Room {data.room.number}</Badge>
            {data.room_type
              ? <Badge className="bg-slate-900 text-white">{data.room_type.name}</Badge>
              : <Badge className="bg-amber-100 border text-amber-800" data-testid="room-setup-pending">Setup Pending — room type not mapped</Badge>}
            {data.templates.length === 0 && data.room_type && <Badge className="bg-amber-100 border text-amber-800">No active template for this room type</Badge>}
          </div>

          {data.tagged_units.length > 0 && (
            <Card className="p-4 mb-4">
              <div className="section-sub mb-2 font-medium text-slate-700">Tagged Assets in Room</div>
              <div className="flex flex-wrap gap-2">
                {data.tagged_units.map(u => (
                  <Badge key={u.id} variant="outline" className="border-slate-300 font-mono">{u.asset_id} · {u.item_name}</Badge>
                ))}
              </div>
            </Card>
          )}

          {data.setup_pending ? (
            <Card className="p-6 text-center text-sm text-slate-500" data-testid="checklist-blocked">
              Daily checklist is unavailable until the room type is mapped and its amenity template is activated (Admin → Asset Settings).
            </Card>
          ) : (
            <Card className="p-4 mb-4">
              <div className="section-sub mb-3 font-medium text-slate-700">Daily Amenity Checklist</div>
              <div className="overflow-x-auto">
                <table className="vr-table" data-testid="checklist-table">
                  <thead><tr><th>Item</th><th className="num">Required</th><th className="num">Previous</th><th className="num">Issued</th><th className="num">Consumed</th><th className="num">Returned</th><th className="num">Missing</th><th className="num">Damaged</th></tr></thead>
                  <tbody>
                    {lines.map((l, idx) => (
                      <tr key={l.item_id}>
                        <td>{itemName(l.item_id)}{!l.mandatory && <span className="text-[10px] text-slate-400 ml-1">(optional)</span>}</td>
                        <td className="num font-semibold">{l.required}</td>
                        <td className="num text-slate-500">{l.previous}</td>
                        {["issued", "consumed", "returned", "missing", "damaged"].map(f => (
                          <td className="num" key={f}>
                            <Input type="number" min="0" className="h-9 w-16 ml-auto text-right" value={l[f]}
                              onChange={e => setLine(idx, f, e.target.value)} data-testid={`cl-${f}-${idx}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3">
                {todaySubmitted ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-emerald-100 border text-emerald-800" data-testid="checklist-done-today">Checklist for {entryDate} already submitted</Badge>
                    {canEdit && <Button size="sm" variant="outline" className="h-9" onClick={openEdit} data-testid="edit-checklist-btn">Edit Checklist</Button>}
                  </div>
                ) : (
                  <Button className="bg-slate-900 text-white hover:bg-slate-800 h-11" onClick={submitChecklist} disabled={submitBusy} data-testid="submit-checklist">{submitBusy ? "Submitting…" : "Submit Checklist"}</Button>
                )}
                <div className="text-xs text-slate-400 mt-1">Entry date can be any past day back to the asset baseline through today (Asia/Kolkata); future dates are blocked. One checklist per room per entry date. Corrections are audited: the original movements are voided and corrected values are re-posted. Issued pulls from Store; consumed, returned, and damaged quantities move out of this room.</div>
              </div>
            </Card>
          )}

          {data.linen_readonly.length > 0 && (
            <Card className="p-4 mb-4 bg-slate-50" data-testid="linen-readonly">
              <div className="section-sub mb-2 font-medium text-slate-700">Linen in Room <Badge variant="outline" className="ml-1 text-[10px]">Read-only — managed by Laundry ledger</Badge></div>
              <div className="flex flex-wrap gap-3 text-sm">
                {data.linen_readonly.map((l, i) => <span key={l.name || i} className="text-slate-600">{l.name}: <strong>{l.qty}</strong></span>)}
              </div>
            </Card>
          )}

          {checklists.length > 0 && (
            <Card className="p-0 overflow-x-auto">
              <table className="vr-table">
                <thead><tr><th>Date</th><th>Status</th><th>By</th><th className="num">Lines</th></tr></thead>
                <tbody>{checklists.slice(0, 10).map(c => (
                  <tr key={c.id}><td>{c.effective_date || c.date}</td>
                    <td><Badge className={c.status === "submitted" ? "bg-emerald-100 border text-emerald-800" : "bg-amber-100 border text-amber-800"}>{c.status}</Badge></td>
                    <td className="text-xs">{c.submitted_by_name || c.created_by_name}</td><td className="num">{c.lines?.length}</td></tr>
                ))}</tbody>
              </table>
            </Card>
          )}
        </>
      )}

      <Dialog open={editOpen} onOpenChange={o => { if (!o) { setEditOpen(false); setShowPreview(false); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="edit-checklist-dialog">
          <DialogHeader>
            <DialogTitle>{showPreview ? "Preview changes" : "Edit Submitted Checklist"} — Room {data?.room?.number} <span className="text-slate-400 font-normal">({submittedCl?.date})</span></DialogTitle>
          </DialogHeader>
          {!showPreview ? (
            <div className="space-y-3">
              <div className="rounded-md bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-800">Editing re-posts stock movements. The original entries are voided (kept for audit) and the corrected values applied. Any open incidents from this checklist will be cancelled and re-created as needed.</div>
              <div className="overflow-x-auto">
                <table className="vr-table" data-testid="edit-checklist-table">
                  <thead><tr><th>Item</th><th className="num">Required</th><th className="num">Previous</th><th className="num">Issued</th><th className="num">Consumed</th><th className="num">Returned</th><th className="num">Missing</th><th className="num">Damaged</th></tr></thead>
                  <tbody>
                    {editLines.map((l, idx) => (
                      <tr key={l.item_id}>
                        <td>{itemName(l.item_id)}</td>
                        <td className="num font-semibold">{l.required}</td>
                        <td className="num text-slate-500">{l.previous}</td>
                        {["issued", "consumed", "returned", "missing", "damaged"].map(f => (
                          <td className="num" key={f}>
                            <Input type="number" min="0" className="h-9 w-16 ml-auto text-right" value={l[f]}
                              onChange={e => setEditLine(idx, f, e.target.value)} data-testid={`edit-cl-${f}-${idx}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <label className="text-sm text-slate-600">Reason for edit (optional)</label>
                <Input value={editReason} onChange={e => setEditReason(e.target.value)} className="h-10 mt-1" placeholder="e.g. Wrong quantity entered for towels" data-testid="edit-reason" />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {changedLines().length === 0 ? (
                <div className="text-center text-slate-500 py-6" data-testid="edit-no-changes">No changes to apply.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="vr-table" data-testid="edit-preview-table">
                    <thead><tr><th>Item</th><th>Field</th><th className="num">Before</th><th className="num">After</th></tr></thead>
                    <tbody>
                      {changedLines().map(({ line, orig, fields }) => fields.filter(f => Number(line[f] || 0) !== Number(orig[f] || 0)).map(f => (
                        <tr key={line.item_id + f}>
                          <td>{itemName(line.item_id)}</td>
                          <td className="capitalize">{f}</td>
                          <td className="num text-slate-500">{Number(orig[f] || 0)}</td>
                          <td className="num font-semibold text-emerald-700">{Number(line[f] || 0)}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {!showPreview ? (
              <>
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={editBusy}>Cancel</Button>
                <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setShowPreview(true)} data-testid="edit-preview-btn">Preview Changes</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowPreview(false)} disabled={editBusy}>Back</Button>
                <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={reviseChecklist} disabled={editBusy || changedLines().length === 0} data-testid="edit-confirm-btn">Confirm & Save</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
